import { useNavigate } from 'react-router-dom'
import { useAsyncResource } from '../hooks/useAsyncResource'
import { repositoryAPI } from '../services/api'
import { getDeviceTypeLabel } from '../utils/deviceType'
import ShareLinkButton from './ShareLinkButton'
import './FeaturedRepositories.css'

const FEATURED_COUNT = 6

async function loadFeaturedWithBackfill(signal) {
  const response = await repositoryAPI.featured({ signal })
  const featured = Array.isArray(response?.repositories) ? response.repositories : []

  if (featured.length >= FEATURED_COUNT) {
    return featured.slice(0, FEATURED_COUNT)
  }

  const fallback = await repositoryAPI.list({
    page: 1,
    page_size: FEATURED_COUNT,
    sort: 'updated',
    signal,
  })
  const fallbackRepos = Array.isArray(fallback?.repositories) ? fallback.repositories : []
  const seen = new Set(featured.map((repo) => repo.id))
  const merged = [...featured]

  fallbackRepos.forEach((repo) => {
    if (merged.length < FEATURED_COUNT && !seen.has(repo.id)) {
      merged.push(repo)
      seen.add(repo.id)
    }
  })

  return merged.slice(0, FEATURED_COUNT)
}

function FeaturedRepositories() {
  const navigate = useNavigate()
  const { data, loading } = useAsyncResource(
    (signal) => loadFeaturedWithBackfill(signal),
    []
  )
  const featuredRepositories = Array.isArray(data) ? data : []

  const handleViewAllClick = (e) => {
    e.preventDefault()
    navigate('/repositories')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRepositoryClick = (e, repoSlug) => {
    e.preventDefault()
    navigate(`/repositories/${repoSlug}`)
  }

  return (
    <section className="featured-repositories">
      <div className="featured-header">
        <h2>Featured Repositories</h2>
        {!loading && featuredRepositories.length > 0 && (
          <a href="/repositories" className="view-all-link" onClick={handleViewAllClick}>
            View All →
          </a>
        )}
      </div>

      {loading ? (
        <p className="loading-message">Loading featured repositories...</p>
      ) : featuredRepositories.length === 0 ? (
        <p className="empty-message">No featured repositories available at this time.</p>
      ) : (
        <div className="repositories-grid">
          {featuredRepositories.map((repo) => {
            const repoSlug = repo.slug || repo.id
            const repoPath = repoSlug ? `/repositories/${repoSlug}` : null
            return (
            <div key={repo.id} className="repository-card-with-share">
              <ShareLinkButton path={repoPath} variant="card" />
              <a
                href={repoPath || '#'}
                onClick={(e) => repoSlug && handleRepositoryClick(e, repoSlug)}
                className="repository-card"
              >
              <div className="repository-header">
                <div className="repository-title-section">
                  <h3>{repo.title}</h3>
                  <span className={`visibility-badge visibility-${repo.visibility}`}>{repo.visibility}</span>
                </div>
                {getDeviceTypeLabel(repo) && <span className="device-type-badge">{getDeviceTypeLabel(repo)}</span>}
              </div>
              <div className="repository-meta-row">
                {repo.manufacturer && (
                  <>
                    <span className="repository-manufacturer">{repo.manufacturer}</span>
                    {repo.year && <span className="repository-year">• {repo.year}</span>}
                    {repo.form_factor && <span className="repository-form-factor">• {repo.form_factor}</span>}
                  </>
                )}
              </div>
              {repo.description && <p className="repository-description">{repo.description}</p>}
              {repo.tags && repo.tags.length > 0 && (
                <div className="repository-tags">
                  {repo.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="tag-chip">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="repository-meta">
                <span className="version-count">
                  <strong>{repo.commit_count || 0}</strong> commits • <strong>{repo.version_count || 0}</strong>{' '}
                  versions
                </span>
              </div>
            </a>
            </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default FeaturedRepositories
